import classname from "classnames";
import React from "react";
import { useRenderOn } from "src/hooks";
import styled from "styled-components";

import profileDesktop from "../../../../../../../assets/images/profileDesktop.png";
import { IWithMe } from "../../../../../../../hoc/repository/withMe/withMe";
import { translate } from "../../../../../../../services/translation.service";
import theme, { media } from "../../../../../../../theme";
import { SignInForm } from "../../../../../../Form";
import Hyperlink from "../../../../../../Hyperlink";
import Icon from "../../../../../../Icon";

const Wrapper = styled.div`
  cursor: pointer;
  display: flex;
  flex-basis: auto;
  align-items: center;
  padding: 13px 16px;
  border-bottom: 1px solid ${theme.colors.grey};

  ${media.laptop} {
    border: none;

    .greetings {
      font-family: ${theme.fonts.regular};
      color: ${theme.colors.white};

      .hello {
        color: ${theme.colors.grey};
      }
    }
  }
`;

const MiniLogin = styled.div`
  ${Wrapper} {
    cursor: pointer;
  }
  &:hover span.hello {
    color: ${theme.colors.white};
  }
`;

const Col = styled.div`
  display: flex;
  flex-direction: column;

  .name {
    display: inline;
  }
`;

const Spacer = styled.span`
  flex-grow: 1;
  flex-shrink: 1;
`;

const StyledIcon = styled(Icon)`
  margin-right: 10px;
`;

const Text = styled.div`
  margin: 0;
  font-family: ${theme.fonts.title};
  font-size: ${theme.fontSizes.medium};
`;

const LogoutHyperlink = styled(Hyperlink)`
  display: inline-block;
  margin-top: 5px;
`;

const WrapperDropdown = styled.div`
  position: absolute;
  z-index: 1;
  right: 0;
  background-color: ${theme.colors.white};
  border: 1px solid ${theme.colors.lightGrey};
  display: flex;
  flex-direction: column;
  margin-top: -${theme.margins.marginMedium};
  width: 250px;

  ${media.laptop} {
    box-shadow: 4px 10px 30px rgba(0, 0, 0, 0.5);

    &.logged {
      padding: ${theme.margins.marginBig};
      color: ${theme.colors.black};
      align-items: flex-start;

      a:not(.logout) {
        &:hover span {
          text-decoration: underline;
          color: ${theme.colors.goldDesktop};
        }

        &:before {
          content: ">";
          margin-right: 10px;
        }
      }

      .logout {
        align-self: flex-end;
        color: ${theme.colors.grey};
        margin-top: 20px;
        text-decoration: underline;
      }
    }
    .signin-form-wrapper {
      .signin-form {
        padding: ${theme.margins.marginBig};
        margin: 0;

        & > div {
          padding: 0;
          justify-content: flex-start;

          h2 {
            font-weight: 400;
            text-transform: uppercase;
            font-size: ${theme.fontSizes.tiny};
          }
        }

        section.bodywrapper {
          padding: 0;
        }

        button[type="submit"] {
          background-color: ${theme.colors.redDesktop};
          box-shadow: 0 -3px 0 ${theme.colors.darkRedDesktop} inset;
          border-radius: 4px;
          padding: 0 40px;
          margin: 20px auto 0 0;
          bottom: initial;
        }

        .forgotten-password {
          font-size: ${theme.fontSizes.tiny};
          letter-spacing: 0;
          color: ${theme.colors.grey};
          cursor: pointer;
          text-decoration: underline;

          &:hover {
            text-decoration: none;
          }
        }
      }
      & > button {
        border-top: 1px solid #e2ddd7;
        background-color: #f7f6f6;
        text-align: left;
        font-size: ${theme.fontSizes.tiny};
        letter-spacing: 0;
        margin: ${theme.margins.marginBase} 0 0 0;
        padding: ${theme.margins.marginBase};
        color: #7d654d;
        text-decoration: underline;

        &:hover {
          text-decoration: initial;
        }

        &:before {
          content: "Nouveaux clients > ";
          color: initial;
          text-decoration: none;
        }
      }
    }
  }
`;

interface IIcon {
  name: string;
  size: number;
}
const DesktopIcon = styled.i<IIcon>`
  display: flex;
  align-items: center;

  &:before {
    content: "";
    display: inline-block;
    width: ${({ size }: IIcon): number => size}px;
    height: ${({ size }: IIcon): number => size}px;
    background-image: url(${({ name }: IIcon): string | undefined => name});
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
    transform: translateY(1%);
    margin-right: 5px;
  }
`;

export interface IProps extends IWithMe {
  className?: string;
  closeMenu?: () => void;
  isLoginOpen?: boolean;
  toggleLogin?: () => void;
}

const AppContentMenuHeader = ({
  className,
  closeMenu,
  me,
  toggleLogin,
  isLoginOpen,
}: IProps): JSX.Element => {
  const { mdOrDown } = useRenderOn();

  const Dropdown = ({ isOpen }: { isOpen: boolean }): JSX.Element | null => {
    const preventClick = (e: React.MouseEvent): void => e.stopPropagation();

    if (isOpen) {
      return me ? (
        <WrapperDropdown className="logged" onClick={preventClick}>
          <Hyperlink to={translate("links.customerAccount")}>
            <span>{translate("menu.myAccount")}</span>
          </Hyperlink>
          <Hyperlink to={translate("links.wishlist")}>
            <span>{translate("menu.myWishlist")}</span>
          </Hyperlink>
          <Hyperlink to={translate("links.orders")}>
            <span>{translate("menu.myOrders")}</span>
          </Hyperlink>
          <LogoutHyperlink
            to={translate("links.customerAccountLogout")}
            className="logout"
          >
            {translate("menu.logout")}
          </LogoutHyperlink>
        </WrapperDropdown>
      ) : (
        <WrapperDropdown className="drop" onClick={preventClick}>
          <SignInForm
            redirectUrl={translate("links.home")}
            signUpUrl="/customer/account/create"
            className="signin-form-wrapper"
          />
        </WrapperDropdown>
      );
    } else {
      return null;
    }
  };

  const renderLogStatus = (): JSX.Element => {
    const loggedGreetings = (
      <>
        <span className="hello">{translate("menu.greetings")}</span>{" "}
        {me ? (
          <div className="name">
            {me.firstName[0].toUpperCase()}.{" "}
            {me.lastName.charAt(0).toUpperCase() + me.lastName.slice(1)}
          </div>
        ) : (
          <div className="name">{translate("menu.account")}</div>
        )}
      </>
    );

    if (me) {
      return mdOrDown ? (
        <Col>
          <Text>
            <Hyperlink to={translate("links.customerAccount")}>
              {loggedGreetings}
            </Hyperlink>
          </Text>
          <LogoutHyperlink to={translate("links.customerAccountLogout")}>
            {translate("menu.logout")}
          </LogoutHyperlink>
        </Col>
      ) : (
        <div>
          <Text className="greetings">{loggedGreetings}</Text>
        </div>
      );
    }

    return mdOrDown ? (
      <Text>
        <Hyperlink to={translate("links.customerAccountLogin")}>
          {translate("menu.signIn")}
        </Hyperlink>
      </Text>
    ) : (
      <Text className="greetings">{loggedGreetings}</Text>
    );
  };

  return mdOrDown ? (
    <Wrapper>
      <StyledIcon name="profile" size={24} onClick={closeMenu} />
      {renderLogStatus()}
      <Spacer />
      <Icon name="cross" size={24} onClick={closeMenu} />
    </Wrapper>
  ) : (
    <MiniLogin className={classname({ open: isLoginOpen }, "mini-login")}>
      <Wrapper onClick={toggleLogin} className={className}>
        <DesktopIcon name={profileDesktop} size={29} />
        {renderLogStatus()}
      </Wrapper>
      <Dropdown isOpen={!!isLoginOpen} />
    </MiniLogin>
  );
};

export default AppContentMenuHeader;
